import { useState } from "react";
import { cn } from "@/lib/utils";

type BrandLogoVariant = "full" | "icon";
type BrandLogoSize = "sm" | "md" | "lg";

interface BrandLogoProps {
  variant?: BrandLogoVariant;
  size?: BrandLogoSize;
  className?: string;
  logoClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  productName?: string;
  subtitle?: string;
  showText?: boolean;
}

const containerSizeClasses: Record<BrandLogoSize, string> = {
  sm: "h-8",
  md: "h-10",
  lg: "h-12",
};

const imageSizeClasses: Record<BrandLogoSize, string> = {
  sm: "h-5",
  md: "h-6",
  lg: "h-8",
};

const titleSizeClasses: Record<BrandLogoSize, string> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
};

const subtitleSizeClasses: Record<BrandLogoSize, string> = {
  sm: "text-[11px]",
  md: "text-xs",
  lg: "text-sm",
};

export default function BrandLogo({
  variant = "full",
  size = "md",
  className,
  logoClassName,
  titleClassName,
  subtitleClassName,
  productName = "AO Manager",
  subtitle,
  showText,
}: BrandLogoProps) {
  const [logoError, setLogoError] = useState(false);
  const displayText = showText ?? variant === "full";

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md border border-border bg-background px-2",
          containerSizeClasses[size],
          logoClassName
        )}
      >
        {logoError ? (
          <span className="text-xs font-semibold tracking-wide text-foreground">
            EPFL
          </span>
        ) : (
          <img
            src="/epfl-logo.png"
            alt="EPFL"
            className={cn("w-auto object-contain", imageSizeClasses[size])}
            onError={() => setLogoError(true)}
          />
        )}
      </div>

      {displayText && (
        <div className="min-w-0">
          <p
            className={cn(
              "truncate font-semibold text-foreground",
              titleSizeClasses[size],
              titleClassName
            )}
          >
            {productName}
          </p>
          {subtitle && (
            <p
              className={cn(
                "truncate text-muted-foreground",
                subtitleSizeClasses[size],
                subtitleClassName
              )}
            >
              {subtitle}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
